'use client';

import React, { useEffect, useRef, useState } from 'react';
import Footer from './components/Footer';
import Header from './components/header/Header';
import MobileMenu from './components/MobileMenu';
import { useAuth0 } from '@auth0/auth0-react';
import { usePathname, useRouter } from 'next/navigation';

// Qué barra superior lleva cada página (ver Header): la home, transparente; /negocios, de vidrio
// sobre el mapa; el resto, blanca. Una página puede forzarla con `headerVariant`.
const variantFor = (pathname, isLanding) => {
  if (isLanding) return 'landing';
  if (pathname === '/negocios') return 'map';
  return 'bar';
};

// `fullHeight={false}`: páginas cortas (404, ficha que ya no está) donde el footer va pegado al
// contenido; con el alto mínimo de pantalla quedaba una franja blanca vacía antes del footer.
// `breadcrumb`: ruta de la barra blanca (`[{ label, href }]`); en su lugar no va el buscador.
const MainLayout = ({
  children,
  hasFooter = true,
  isLanding = false,
  isAuth = false,
  footerData,
  fullHeight = true,
  headerVariant,
  breadcrumb = null,
  hideHeaderSearch
}) => {
  const pathname = usePathname();
  const variant = headerVariant || variantFor(pathname, isLanding);
  // La ficha de un negocio no lleva buscador en la barra (como en la propuesta).
  const hideSearch = hideHeaderSearch ?? /^\/negocios\/[^/]+\/?$/.test(pathname || '');
  const [isToggled, setToggled] = useState(false);
  const handleToggle = () => setToggled(!isToggled);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);
  

  const router = useRouter();

  const { isAuthenticated } = useAuth0();

  if (!isAuthenticated && isAuth) {
    router.push('/');
  }

  useEffect(() => {
    // Apenas se mueve: el navbar transparente del home solo vale arriba de todo (si no, el contenido
    // pasa por detrás del logo).
    const onScroll = () => setIsScrolled(window.scrollY > 4);

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const heroElement = document.getElementById('hero');
    if (heroElement) {
      const heroObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setShowSearchBar(false);
            } else {
              setShowSearchBar(true);
            }
          });
        },
        { 
          rootMargin: '-100px 0px -100px 0px', // More moderate margins
          threshold: 0.1 
        }
      );

      heroObserver.observe(heroElement);

      return () => heroObserver.unobserve(heroElement);
    }
  }, [children]);

  return (
    <>
      <header>
        <Header
          handleToggle={handleToggle}
          scroll={isScrolled}
          variant={variant}
          showSearchBar={showSearchBar}
          breadcrumb={breadcrumb}
          hideSearch={hideSearch}
        />
        <MobileMenu isToggled={isToggled} handleToggle={handleToggle} />
      </header>
      {/* La portada va debajo de la barra transparente; el resto, después de los 72px de la barra. En
          /negocios la barra flota (16px de margen, 64 de alto) dentro de esos mismos 72px; si el mapa
          quiere ir a sangre por debajo, lo hace la página (posición fija), no el layout. */}
      <main
        className={
          variant === 'landing' ? '' : fullHeight && variant !== 'map' ? 'mt-[72px] min-h-[calc(100svh-72px)]' : 'mt-[72px]'
        }
      >
        {children}
      </main>
      {hasFooter && <Footer categories={footerData?.categories} provinces={footerData?.provinces} />}
    </>
  );
};

export default MainLayout;
